import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { getCookie } from "../../utility/getCookie";
import {
  TeamsState,
  InfinityCommunityMember,
  ReferralCommunityMember,
} from "./types";

const API_URL = `${process.env.REACT_APP_API_BASE_URL}/api/v1/referral-mlm/`;

const CUSTOMER_API_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_CUSTOMER_APP_BACKEND_PREFIX,
]
  .filter(Boolean)
  .join("");

// Axios instance with cookies support
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add Bearer token from cookies
api.interceptors.request.use(
  async (config) => {
    let token = getCookie("access_token");
    if (token === null || token === undefined) {
      try {
        token = getCookie("access_token");
      } catch (err) {
        // Handle refresh failure (e.g., redirect to login)
      }
    }
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

const customerApi = axios.create({
  baseURL: CUSTOMER_API_URL,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

customerApi.interceptors.request.use(
  async (config) => {
    const token = getCookie("access_token");
    if (token) config.headers["Authorization"] = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error),
);

export const getAllInfinityMembers = createAsyncThunk(
  "teams/getAllInfinityMembers",
  async (
    { page = 1, limit = 10 }: { page?: number; limit?: number } = {},
    { rejectWithValue },
  ) => {
    try {
      const response = await api.get(
        `infinity-community/all?page=${page}&limit=${limit}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch all infinity members",
      );
    }
  },
);

export const getInfinityMembersByLevel = createAsyncThunk(
  "teams/getInfinityMembersByLevel",
  async (
    {
      level,
      page = 1,
      limit = 10,
    }: { level: number; page?: number; limit?: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.get(
        `infinity-community/level/${level}?page=${page}&limit=${limit}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          `Failed to fetch infinity members for level ${level}`,
      );
    }
  },
);

export const getMyReferrals = createAsyncThunk(
  "teams/getMyReferrals",
  async (_, { rejectWithValue }) => {
    try {
      const response = await customerApi.get("/referral-community/my-referrals");
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch referral members",
      );
    }
  },
);

const initialState: TeamsState = {
  infinityMembers: {},
  infinityMembersPagination: null,
  infinityMembersByLevel: [],
  infinityMembersByLevelPagination: null,
  referralMembers: [],
  loading: false,
  error: null,
  totalreferralMembers:0,
};

const teamsSlice = createSlice({
  name: "teams",
  initialState,
  reducers: {
    clearTeamsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // getAllInfinityMembers
      .addCase(getAllInfinityMembers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getAllInfinityMembers.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.infinityMembers = action.payload.data;
          state.infinityMembersPagination = {
            currentPage: action.payload.currentPage,
            totalPages: action.payload.totalPages,
            pageSize: action.payload.pageSize,
            totalMembers: action.payload.totalMembers,
            totalLevels: action.payload.totalLevels,
          };
        },
      )
      .addCase(getAllInfinityMembers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // getInfinityMembersByLevel
      .addCase(getInfinityMembersByLevel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getInfinityMembersByLevel.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.infinityMembersByLevel = action.payload.data;
          state.infinityMembersByLevelPagination = {
            currentPage: action.payload.currentPage,
            totalPages: action.payload.totalPages,
            pageSize: action.payload.pageSize,
            totalMembers: action.payload.totalMembers,
          };
        },
      )
      .addCase(getInfinityMembersByLevel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // getMyReferrals
      .addCase(getMyReferrals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getMyReferrals.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.referralMembers = action.payload.data || [];
          state.totalreferralMembers = action.payload.total;
        },
      )
      .addCase(getMyReferrals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearTeamsError } = teamsSlice.actions;

export default teamsSlice.reducer;
